import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { BUILD_INFO } from './build-info';
import { isStaleBundleError, reloadForStaleBundle } from './stale-bundle';

interface VersionManifest {
    buildId: string;
}

const CHECK_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Keeps long-lived tabs on the latest deployed build without user action.
 *
 * The pipeline bakes a build id into the bundle (BUILD_INFO) and publishes the
 * same id as /version.json next to index.html. This service polls the manifest
 * (every 10 min + whenever the tab regains focus) and, when the server has a
 * newer build, reloads at the least disruptive moment:
 *
 *  - tab hidden        -> reload immediately (invisible to the user)
 *  - tab visible       -> reload on the next router navigation, which already
 *                         discards page state, so the full load is seamless
 *  - chunk-load errors -> reload right away toward the attempted route
 *                         (the old hashed chunks are gone after a deploy)
 *
 * Disabled entirely for local dev builds (buildId === 'local').
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
    private readonly _router = inject(Router);
    private readonly _zone = inject(NgZone);

    private _updatePending = false;
    private _checking = false;

    initialize(): void {
        if (BUILD_INFO.buildId === 'local') {
            return;
        }

        // Reload the moment a navigation lands while an update is pending, or
        // when a navigation dies because its lazy chunk vanished in a deploy.
        this._router.events.subscribe((event) => {
            if (event instanceof NavigationEnd && this._updatePending) {
                reloadForStaleBundle();
            } else if (
                event instanceof NavigationError &&
                isStaleBundleError(event.error)
            ) {
                reloadForStaleBundle(event.url);
            }
        });

        // Timers and DOM listeners outside Angular so polling never triggers
        // change detection.
        this._zone.runOutsideAngular(() => {
            setInterval(() => void this._check(), CHECK_INTERVAL_MS);

            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    // Backgrounded tab: apply a pending update invisibly.
                    if (this._updatePending) {
                        reloadForStaleBundle();
                    }
                } else {
                    // Tab regained focus after possibly hours — check now.
                    void this._check();
                }
            });
        });
    }

    private async _check(): Promise<void> {
        if (this._checking || this._updatePending) {
            if (this._updatePending && document.hidden) {
                reloadForStaleBundle();
            }
            return;
        }
        this._checking = true;

        try {
            const url = new URL('version.json', document.baseURI);
            url.searchParams.set('t', String(Date.now()));

            // Plain fetch: same-origin static file, must bypass the HTTP
            // interceptor chain (auth / function key / MSAL) and every cache.
            const response = await fetch(url.toString(), { cache: 'no-store' });
            if (!response.ok) {
                return;
            }

            const manifest = (await response.json()) as VersionManifest;
            if (manifest.buildId && manifest.buildId !== BUILD_INFO.buildId) {
                this._updatePending = true;
                if (document.hidden) {
                    reloadForStaleBundle();
                }
            }
        } catch {
            // Network hiccup or malformed manifest — try again next cycle.
        } finally {
            this._checking = false;
        }
    }
}
